import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRemoteFeatureFlag } from '@nbx/config';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import { SocialLogin } from 'components/app/SocialLogin';
import ButtonText from 'components/shared/ButtonText';
import ButtonShowPassword from 'components/shared/ButtonShowPassword';
import FieldsSeparator from 'components/shared/FieldsSeparator';
import Field from 'components/shared/Field';
import FormError from 'components/shared/FormError';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import { getURLParam } from 'helpers/url';
import STEPS from 'constants/steps';
import { clearError, login, setIsEnrollFirebaseSms2faOnSignup } from 'reduxConfig/auth.creators';
import styles from './styles.module.scss';
import { useWriteRedirectAfterLogin } from './useWriteRedirectAfterLogin';

const microapps = window.MICROFRONTENDS_CONFIG;

export function Login({ dispatch, error: apiError, loading, step }) {
  const { t } = useTranslation();

  useWriteRedirectAfterLogin();

  const [values, setValues] = useState({
    email: '',
    password: ''
  });

  const { email, password } = values;

  const [type, setType] = useState('password');
  const [loginMethod, setLoginMethod] = useState(null);

  const [isEnrollFirebaseSms2faOnSignup, isLoadingFeatureFlag] = useRemoteFeatureFlag(
    'auth_enroll_sms_2fa_on_signup_feature_flag'
  );

  useEffect(() => {
    if (isLoadingFeatureFlag === false) {
      dispatch(setIsEnrollFirebaseSms2faOnSignup(isEnrollFirebaseSms2faOnSignup));
    }
  }, [isLoadingFeatureFlag]);

  const handleChange = e => {
    const { id, value } = e.target;
    if (apiError) dispatch(clearError());
    setValues({ ...values, [id]: value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setLoginMethod('email');
    dispatch(login(email, password, emailVerified));
  };

  const setInputType = () => {
    const inputType = type === 'text' ? 'password' : 'text';
    setType(inputType);
  };

  useEffect(() => {
    const queryEmail = getURLParam('email');
    if (queryEmail) {
      setValues({ ...values, email: queryEmail });
    }
  }, [values]);

  const emailVerified = step === STEPS.verifyEmailComplete;
  const title = emailVerified ? t('auth_login_title_verified') : t('auth_login_title');

  if (step === STEPS.verifyCode || step === STEPS.verifySMSCode)
    return <Redirect to={microapps.routes.verifyCode.path} />;
  if (step === STEPS.verifyEmail) return <Redirect to={microapps.routes.verifyEmail.path} />;
  if (step === STEPS.addPhoneNumber) return <Redirect to={microapps.routes.addPhoneNumber.path} />;

  return (
    <div className={styles.loginWrapper}>
      <SEO title={t('auth_login_meta_title')} />
      <Heading tag="h1">{title}</Heading>
      <span id="recaptcha-verifier"></span>
      {emailVerified ? (
        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {t('auth_login_subtitle_verified')}
        </Text>
      ) : (
        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {t('auth_login_subtitle')}{' '}
          <ButtonText to={microapps.routes.signup.path}>{t('common_sign_up')}</ButtonText>
        </Text>
      )}
      <SocialLogin
        dispatch={dispatch}
        loginMethod={loginMethod}
        setLoginMethod={setLoginMethod}
        styles={[styles.bottomMargin]}
        loading={loading}
      />
      <FieldsSeparator styles={[styles.bottomMargin]}>{t('common_or')}</FieldsSeparator>
      <form onSubmit={handleSubmit} autoComplete="on">
        <Field
          id="email"
          name={t('common_email')}
          type="email"
          value={email}
          autoComplete="username"
          onChange={handleChange}
        />
        <Field
          id="password"
          name={t('common_password')}
          type={type}
          value={password}
          autoComplete="current-password"
          onChange={handleChange}
          inlineElements={<ButtonShowPassword onClick={setInputType} />}
        />
        {apiError && <FormError>{t(apiError)}</FormError>}
        <Row>
          <ButtonPrimary
            disabled={!email || !password || (loading && loginMethod === 'email')}
            loading={loading && loginMethod === 'email'}
            type="submit"
            fullWidth
            styles={[styles.bottomMargin]}>
            {t('common_log_in')}
          </ButtonPrimary>
        </Row>

        <Text muted tall>
          {t('auth_login_forgot_password')}
          <ButtonText to={microapps.routes.resetPassword.path} muted underlined styles={[styles.resetLink]}>
            {t('auth_login_request_reset')}
          </ButtonText>
        </Text>
      </form>
    </div>
  );
}

Login.propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string,
  isMobile: PropTypes.bool.isRequired
};

Login.defaultProps = {
  error: null,
  step: null
};
